/**
 * Helpers extracted out of SceneRouter.tsx as part of the M1 split. Pure
 * functions that derive view-state from the SSE projection. Imported by
 * SceneRouter itself and by the per-act renderer registries
 * (lib/scenes/act4.tsx and friends).
 */

import type { EventLogState } from "@/lib/sse/useSSE";

/**
 * String discriminator for the counterpart pane that SceneRouter
 * renders for the I (importer) or E (exporter) override. Pure function
 * — no React, no event-log dependency on shape, just (screen, perspective).
 * Tested in lib/scenes/__tests__/perspective.test.ts so a screen
 * renumber or perspective-routing regression fails the suite, not the
 * live demo.
 *
 *   - "smart-logistics-importer" → SmartLogisticsImporter
 *   - "settlement-importer-t1"   → SettlementImporter variant=t1
 *   - "settlement-importer-t3"   → SettlementImporter variant=t3
 *   - "catalogue-analytics"      → CatalogueAnalytics (Aurora T1 0.5)
 *   - "inbound-proposal-triage"  → InboundProposalTriage (Aurora T1 1.6/1.8)
 *   - "trade-in-progress:<mode>" → TradeInProgressExporter for given mode
 *   - null                       → no counterpart override; natural scene
 */
export type CounterpartKind =
  | "smart-logistics-importer"
  | "settlement-importer-t1"
  | "settlement-importer-t3"
  | "catalogue-analytics"
  | "inbound-proposal-triage"
  | "lc-advised-to-you"
  | "trade-in-progress:onboarding"
  | "trade-in-progress:escrow-in-progress"
  | "trade-in-progress:lc-in-progress"
  | "trade-in-progress:t3-lc-in-progress"
  | null;

export function resolveCounterpartKind(
  screen: string,
  perspective: string,
): CounterpartKind {
  const importer = perspective.startsWith("I-");
  const exporter = perspective === "E" || perspective === "E-GP";

  if (importer) {
    if (screen === "2.5")  return "smart-logistics-importer";
    if (screen === "3.5")  return "settlement-importer-t1";
    if (screen === "5.17") return "settlement-importer-t3";
    return null;
  }

  if (exporter) {
    // Aurora's counterpart scenes take precedence over the generic
    // onboarding placeholder for specific screens where she has agency.
    if (screen === "0.5")                     return "catalogue-analytics";
    if (screen === "1.6" || screen === "1.8") return "inbound-proposal-triage";
    // 4.13 — Aurora's beneficiary framing of T2's LC issuance.
    if (screen === "4.13")                    return "lc-advised-to-you";

    if (/^1\./.test(screen))                                                    return "trade-in-progress:onboarding";
    if (screen === "3.1" || screen === "3.2" || screen === "3.3")               return "trade-in-progress:escrow-in-progress";
    if (screen === "4.10" || screen === "4.11" || screen === "4.12")            return "trade-in-progress:lc-in-progress";
    if (screen === "5.4")                                                        return "trade-in-progress:t3-lc-in-progress";
    if (screen === "5.11" || screen === "5.12")                                  return "trade-in-progress:t3-lc-in-progress";
    return null;
  }

  return null;
}

/**
 * Clamp the projection's raw invoice state to the InvoiceFsmTile's
 * accepted display states. MATCHED renders as SINGLE_WINDOW_SUBMITTED
 * because the tile's walk caps at single-window (the MATCHED state is
 * surfaced separately on the settlement screen).
 */
export function pickInvoiceState(
  ev: EventLogState,
  invoiceId: string,
): "DRAFT" | "TRANSMITTED" | "ACCEPTED" | "EXTERNALLY_VALIDATED" | "SINGLE_WINDOW_SUBMITTED" {
  const inv = ev.invoiceState[invoiceId];
  if (inv === "MATCHED" || inv === "SINGLE_WINDOW_SUBMITTED") return "SINGLE_WINDOW_SUBMITTED";
  if (inv === "EXTERNALLY_VALIDATED")                         return "EXTERNALLY_VALIDATED";
  if (inv === "ACCEPTED")                                     return "ACCEPTED";
  if (inv === "TRANSMITTED")                                  return "TRANSMITTED";
  return "DRAFT";
}

/**
 * Filter policy decisions to the LC-side gate cascade for the given
 * scenario (so 3.3 sees only T1 cascade, 4.12 sees only T2 cascade,
 * etc.) and exclude the document-side reconciliation gates which
 * belong to Act 2.3.
 */
export function lcGateDecisions(ev: EventLogState, scenarioId: string) {
  const docSideGates = new Set(["CrossDocumentConsistencyCheck", "WeightToleranceRule"]);
  // Canonical gate ordering — commercial-policy gates first, AML /
  // sanctions / Travel Rule grouped at the END per the latest amendment.
  // Mirrors the compliance-snapshot row ordering on 2.6 (Receita Federal
  // → E-invoicing → Dubai Trade → MOCCAE → Compliance Hard Stops) with
  // the regulatory tail (AML → Sanctions → Travel Rule) last.
  // Gates not in the table fall through below in original order.
  const GATE_ORDER: Record<string, number> = {
    TaxAssessmentValidity:                 0,  // Receita Federal / E-invoicing
    SmartLogisticsAssessment:              1,  // Dubai Trade
    IntentMandate:                         2,  // MOCCAE / commercial mandates
    EffectiveCeiling:                      3,
    LegalPartyOnboarding:                  4,
    StandardsComplianceHardStop_H1:        5,  // Compliance hard stops
    StandardsComplianceHardStop_H2:        6,
    AMLScreening:                          7,  // tail group: AML
    SanctionsListReference:                8,  // tail group: Sanctions
    TravelRule:                            9,  // tail group: Travel Rule
  };
  return ev.policyDecisions
    .filter((d) => d.correlationId === scenarioId)
    .filter((d) => !docSideGates.has(String(d.payload.gate ?? "")))
    .slice()
    .sort((a, b) => {
      const oa = GATE_ORDER[String(a.payload.gate ?? "")] ?? 99;
      const ob = GATE_ORDER[String(b.payload.gate ?? "")] ?? 99;
      return oa - ob;
    });
}

type ReputationDelta = { before: Record<string, unknown>; after: Record<string, unknown> };

/**
 * Pick the per-scenario reputation deltas. Each scenario emits a
 * reputation.Updated event per subject with its own before/after pair —
 * we use those directly rather than the lifetime accumulator in
 * `ev.reputationBefore` so T2 shows 1→2 (its own delta) rather than 0→2.
 *
 * T1/T2 helper: returns alMadar + aurora deltas.
 */
export function scenarioReputation(ev: EventLogState, scenarioId: string): {
  alMadar: ReputationDelta | null;
  aurora:  ReputationDelta | null;
} {
  const scoped = ev.events.filter(
    (e) => e.eventType === "reputation.Updated" && e.correlationId === scenarioId,
  );
  const findFor = (orbid: string) => {
    const last = [...scoped].reverse().find((e) => e.payload.subjectOrbid === orbid);
    if (!last) return null;
    return {
      before: (last.payload.before as Record<string, unknown> | undefined) ?? {},
      after:  (last.payload.after  as Record<string, unknown> | undefined) ?? {},
    };
  };
  return {
    alMadar: findFor("orbid:5493-almadar-ae"),
    aurora:  findFor("orbid:5493-aves-aurora-br"),
  };
}

/** T3 reputation helper — Pampagro + Gulf Phosphate deltas. */
export function scenarioReputationT3(ev: EventLogState): {
  pampagro:      ReputationDelta | null;
  gulfPhosphate: ReputationDelta | null;
} {
  const scoped = ev.events.filter(
    (e) => e.eventType === "reputation.Updated" && e.correlationId === "t3-reverse-corridor",
  );
  const findFor = (orbid: string) => {
    const last = [...scoped].reverse().find((e) => e.payload.subjectOrbid === orbid);
    if (!last) return null;
    return {
      before: (last.payload.before as Record<string, unknown> | undefined) ?? {},
      after:  (last.payload.after  as Record<string, unknown> | undefined) ?? {},
    };
  };
  return {
    pampagro:      findFor("orbid:5493-pampagro-br"),
    gulfPhosphate: findFor("orbid:5493-gulfphosphate-ae"),
  };
}

/** T4 reputation helper — Verde Carnes + Al Bayt deltas. */
export function scenarioReputationT4(ev: EventLogState): {
  verdeCarnes: ReputationDelta | null;
  alBayt:      ReputationDelta | null;
} {
  const scoped = ev.events.filter(
    (e) => e.eventType === "reputation.Updated" && e.correlationId === "t4-air-cargo",
  );
  const findFor = (orbid: string) => {
    const last = [...scoped].reverse().find((e) => e.payload.subjectOrbid === orbid);
    if (!last) return null;
    return {
      before: (last.payload.before as Record<string, unknown> | undefined) ?? {},
      after:  (last.payload.after  as Record<string, unknown> | undefined) ?? {},
    };
  };
  return {
    verdeCarnes: findFor("orbid:5493-verdecarnes-br"),
    alBayt:      findFor("orbid:5493-albayt-ae"),
  };
}
